import React, { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react';
import { Appearance, Platform, Settings } from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';

const storage = Platform.OS === 'macos' ? {
  getItem: (key) => Promise.resolve(Settings.get(key) ?? null),
  setItem: (key, value) => { Settings.set({ [key]: value }); return Promise.resolve(); },
} : AsyncStorage;
import { Themes, ACCENT_COLORS, ACCENT_TEXT_COLORS, THEME_DEFAULT_ACCENT, isDarkTheme } from '@/constants/Colors';
import { DEFAULT_ACCENT_COLOR, DEFAULT_ACCENT_TEXT_COLOR, BORDER_RADIUS } from '@/constants/config';
import {
  ACCENT_BG_STORAGE_KEY,
  ACCENT_TEXT_STORAGE_KEY,
  readStoredAccentPreference,
  readStoredThemePreference,
  resolveThemeName,
} from '@/lib/bootTheme';
import { useAuthStore } from '@/store';
import { useUIStore } from '@/store/ui';
import useResponsiveness from '@/hooks/useResponsiveness';

// Get device color scheme in a way that updates when OS/browser theme changes.
// On web we use matchMedia so we get change events; on native we use Appearance.
function getDeviceColorScheme() {
  if (typeof window !== 'undefined' && window.matchMedia) {
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  }
  return Appearance.getColorScheme() ?? 'light';
}

const ThemeContext = createContext(null);

// Shared color utility helpers
const hexToRgb = (hex) => {
  const shorthandRegex = /^#?([a-f\d])([a-f\d])([a-f\d])$/i;
  hex = hex.replace(shorthandRegex, (m, r, g, b) => r + r + g + g + b + b);
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result ? {
    r: parseInt(result[1], 16),
    g: parseInt(result[2], 16),
    b: parseInt(result[3], 16)
  } : null;
};

const rgbToHex = (r, g, b) => '#' + [r, g, b]
  .map(x => Math.round(x).toString(16).padStart(2, '0'))
  .join('');

const getLuminance = (r, g, b) => {
  const [rs, gs, bs] = [r, g, b].map(c => {
    c = c / 255;
    return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * rs + 0.7152 * gs + 0.0722 * bs;
};

const getContrastRatio = (hex1, hex2) => {
  const rgb1 = hexToRgb(hex1);
  const rgb2 = hexToRgb(hex2);
  if (!rgb1 || !rgb2) return 21; // assume sufficient contrast if we can't parse
  const l1 = getLuminance(rgb1.r, rgb1.g, rgb1.b);
  const l2 = getLuminance(rgb2.r, rgb2.g, rgb2.b);
  return (Math.max(l1, l2) + 0.05) / (Math.min(l1, l2) + 0.05);
};

// Helper function to adjust color contrast
function adjustBorderContrast(color, background, adjustment = 30) {
  const borderRgb = hexToRgb(color);
  const bgRgb = hexToRgb(background);
  
  if (!borderRgb || !bgRgb) return color;

  const borderLuminance = getLuminance(borderRgb.r, borderRgb.g, borderRgb.b);
  const bgLuminance = getLuminance(bgRgb.r, bgRgb.g, bgRgb.b);

  // Calculate contrast ratio
  const contrastRatio = (Math.max(borderLuminance, bgLuminance) + 0.05) /
                       (Math.min(borderLuminance, bgLuminance) + 0.05);

  if (contrastRatio < 3) {
    // Adjust border color to increase contrast
    const adjustFactor = bgLuminance > 0.5 ? -adjustment : adjustment;
    const newBorderRgb = {
      r: Math.max(0, Math.min(255, borderRgb.r + adjustFactor)),
      g: Math.max(0, Math.min(255, borderRgb.g + adjustFactor)),
      b: Math.max(0, Math.min(255, borderRgb.b + adjustFactor))
    };
    return rgbToHex(newBorderRgb.r, newBorderRgb.g, newBorderRgb.b);
  }

  return color;
}

// Ensure accent text color has sufficient contrast against accent background
function ensureAccentTextContrast(textColor, bgColor) {
  const bgRgb = hexToRgb(bgColor);
  const textRgb = hexToRgb(textColor);
  if (bgRgb && textRgb) {
    const bgLum = getLuminance(bgRgb.r, bgRgb.g, bgRgb.b);
    const textLum = getLuminance(textRgb.r, textRgb.g, textRgb.b);
    // Dark-on-dark passes loose contrast checks but reads as illegible on CTAs (e.g. black on charcoal gray).
    if (textLum < 0.5 && bgLum < 0.5) {
      return '#FFFFFF';
    }
  }
  if (getContrastRatio(textColor, bgColor) < 3) {
    // Not enough contrast — pick ink or white based on bg luminance
    if (!bgRgb) return textColor;
    const bgLum = getLuminance(bgRgb.r, bgRgb.g, bgRgb.b);
    return bgLum > 0.5 ? '#000000' : '#FFFFFF';
  }
  return textColor;
}

function ensureBubbleTextContrast(textColor, bgColor) {
  if (getContrastRatio(textColor, bgColor) < 4.5) {
    const bgRgb = hexToRgb(bgColor);
    if (!bgRgb) return textColor;
    const bgLum = getLuminance(bgRgb.r, bgRgb.g, bgRgb.b);
    return bgLum > 0.5 ? '#111111' : '#FFFFFF';
  }
  return textColor;
}

function ensureLinkTextContrast(linkColor, bgColor, isDark) {
  const ratio = getContrastRatio(linkColor, bgColor);
  if (ratio >= 4.5) return linkColor;
  return isDark ? '#58a6ff' : '#3157a4';
}

function getAccentBackgroundColor(themeName, colorId) {
  const themeDefault = THEME_DEFAULT_ACCENT[themeName] ?? ACCENT_COLORS.find(c => c.id === 'white')?.color;
  if (!colorId || colorId === 'multicolor' || colorId === '#D4D4D4') {
    return themeDefault;
  }
  const foundColor = ACCENT_COLORS.find(c => c.id === colorId);
  return foundColor ? foundColor.color : themeDefault;
}

function getAccentTextColor(colorId) {
  if (!colorId) {
    return DEFAULT_ACCENT_TEXT_COLOR;
  }
  const foundColor = ACCENT_TEXT_COLORS.find(c => c.id === colorId);
  return foundColor ? foundColor.color : DEFAULT_ACCENT_TEXT_COLOR;
}

function normalizeStoredAccentBackground(stored) {
  const normalized = stored === '#D4D4D4' ? 'multicolor' : stored;
  if (
    normalized
    && (ACCENT_COLORS.some(c => c.id === normalized) || normalized === DEFAULT_ACCENT_COLOR)
  ) {
    return normalized;
  }
  return null;
}

function normalizeStoredAccentText(stored) {
  if (
    stored
    && (ACCENT_TEXT_COLORS.some(c => c.id === stored) || stored === DEFAULT_ACCENT_TEXT_COLOR)
  ) {
    return stored;
  }
  return null;
}

// Helper function to get border radius value
function getBorderRadius(size, roundedEverything = false) {
  if (roundedEverything) {
    return BORDER_RADIUS.ROUNDED;
  }

  const isSmall = ['s', 'small'].includes(size);
  const isMedium = ['m', 'medium'].includes(size);
  const isLarge = ['l', 'large'].includes(size);
  const isRounded = ['r', 'rounded'].includes(size);

  if (isSmall) {
    return 4;
  } else if (isMedium) {
    return 8;
  } else if (isLarge) {
    return 12;
  } else if (isRounded) {
    return BORDER_RADIUS.ROUNDED;
  }

  return 0;
}

// `forceTheme` (optional): when provided, this provider ignores the user's
// stored preference and OS color scheme and renders the given theme name
// (e.g. "white" or "black") instead. The user's saved preferences are NOT
// modified — this is a render-time override only. `setTheme` is a no-op in
// this mode. Use sparingly (e.g. a preview or embedded surface); the app
// root uses a normal provider so theme changes persist.
// Scoped providers theme their descendants without changing the app's HTML root.
export function ThemeProvider({ children, forceTheme = null, scoped = false }) {
  const isForced = !!forceTheme && !!Themes[forceTheme];
  const webTheme = !isForced && Platform.OS === 'web' ? readStoredThemePreference() : null;
  const webAccentBackground = !isForced && Platform.OS === 'web'
    ? normalizeStoredAccentBackground(readStoredAccentPreference(ACCENT_BG_STORAGE_KEY))
    : null;
  const webAccentText = !isForced && Platform.OS === 'web'
    ? normalizeStoredAccentText(readStoredAccentPreference(ACCENT_TEXT_STORAGE_KEY))
    : null;

  const [theme, setThemeRaw] = useState(webTheme);
  const [hasExplicitTheme, setHasExplicitTheme] = useState(!!webTheme);
  const [accentBackgroundColor, setAccentBackgroundColor] = useState(
    isForced ? DEFAULT_ACCENT_COLOR : webAccentBackground
  );
  const [accentTextColor, setAccentTextColor] = useState(
    isForced ? DEFAULT_ACCENT_TEXT_COLOR : webAccentText
  );
  // ANDROID ONLY: paint immediately so One UI dropping the system splash cannot
  // leave a null-gated black window. Do not add `Platform.OS !== 'web'` here —
  // that shipped in f01b59c9, painted the logged-out header on desktop hard
  // refresh, and was rolled back to Android-only on purpose.
  // Web and iOS keep the loading gate: web so auth/theme hydrate first; iOS
  // because the splash still covers it.
  const [isLoading, setIsLoading] = useState(
    !isForced && Platform.OS !== 'android'
  );
  const [deviceColorScheme, setDeviceColorScheme] = useState(() => getDeviceColorScheme());
  const increaseBorderContrast = useUIStore(state => state.increaseBorderContrast);
  const { isXS } = useResponsiveness();
  const isDesktop = Platform.OS === 'macos' || (Platform.OS === 'web' && !isXS);
  // Decides which default applies to a device that has never picked a theme:
  // dark in the app, light on the logged-out marketing pages.
  const isSignedIn = useAuthStore(state => !!state.token);

  // When the user explicitly picks a theme, mark it so we stop following device theme.
  // Forced providers ignore this entirely — they never persist or propagate.
  const setTheme = useCallback((newTheme) => {
    if (isForced) return;
    setHasExplicitTheme(true);
    setThemeRaw(newTheme);
  }, [isForced]);

  // Keep device color scheme in sync when OS/browser theme changes
  useEffect(() => {
    if (isForced) return;
    if (Platform.OS === 'web' && typeof window !== 'undefined' && window.matchMedia) {
      const mq = window.matchMedia('(prefers-color-scheme: dark)');
      const handleChange = () => setDeviceColorScheme(mq.matches ? 'dark' : 'light');
      mq.addEventListener('change', handleChange);
      return () => mq.removeEventListener('change', handleChange);
    }
    const sub = Appearance.addChangeListener(({ colorScheme }) => {
      setDeviceColorScheme(colorScheme ?? 'light');
    });
    return () => sub.remove();
  }, [isForced]);

  // Load theme and colors on mount
  useEffect(() => {
    if (isForced) return;
    let cancelled = false;
    const loadThemeSettings = async () => {
      try {
        const [storedTheme, storedAccentBackgroundColor, storedAccentTextColor] = await Promise.all([
          storage.getItem('theme'),
          storage.getItem('accentBackgroundColor'),
          storage.getItem('accentTextColor')
        ]);
        if (cancelled) return;
        
        // 'auto' is a stored choice like any other even though it has no
        // palette of its own — it resolves against the OS in getActiveTheme.
        if (storedTheme && (Themes[storedTheme] || storedTheme === 'auto')) {
          setThemeRaw(storedTheme);
          setHasExplicitTheme(true);
        }
        setAccentBackgroundColor(
          normalizeStoredAccentBackground(storedAccentBackgroundColor) || DEFAULT_ACCENT_COLOR
        );
        setAccentTextColor(
          normalizeStoredAccentText(storedAccentTextColor) || DEFAULT_ACCENT_TEXT_COLOR
        );
      } catch (error) {
        if (cancelled) return;
        console.error('Error loading theme settings:', error);
        setAccentBackgroundColor(DEFAULT_ACCENT_COLOR);
        setAccentTextColor(DEFAULT_ACCENT_TEXT_COLOR);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    loadThemeSettings();
    return () => { cancelled = true; };
  }, [isForced]);

  // Save theme to storage whenever it changes (skip when forced — forced
  // providers must never write to storage or they'd diverge from a parent
  // provider that shares the same storage keys).
  useEffect(() => {
    if (isForced) return;
    if (theme) {
      storage.setItem('theme', theme).catch(error => {
        console.error('Error saving theme:', error);
      });
    }
  }, [theme, isForced]);

  // Save account background color to storage whenever it changes
  useEffect(() => {
    if (isForced) return;
    if (accentBackgroundColor) {
      storage.setItem('accentBackgroundColor', accentBackgroundColor).catch(error => {
        console.error('Error saving account background color:', error);
      });
    }
  }, [accentBackgroundColor, isForced]);

  // Save accent text color to storage whenever it changes
  useEffect(() => {
    if (isForced) return;
    if (accentTextColor) {
      storage.setItem('accentTextColor', accentTextColor).catch(error => {
        console.error('Error saving accent text color:', error);
      });
    }
  }, [accentTextColor, isForced]);

  // A theme the user picked wins everywhere. Until they pick one they get the
  // default for the surface they're on rather than whatever the OS prefers;
  // following the OS is opt-in via the explicit 'auto' value.
  const getActiveTheme = () => {
    if (isForced) return forceTheme;
    return resolveThemeName({
      storedTheme: hasExplicitTheme ? theme : null,
      isSignedIn,
      prefersDark: deviceColorScheme === 'dark',
    });
  };

  const activeThemeName = getActiveTheme();

  // Add useEffect to set theme attribute on root HTML element
  useEffect(() => {
    if (scoped || Platform.OS !== 'web' || typeof document === 'undefined') return;
    document.documentElement.setAttribute('theme', activeThemeName);
  }, [activeThemeName, scoped]);

  // Add useEffect to set theme attribute on root HTML element
  useEffect(() => {
    if (scoped || Platform.OS !== 'web' || typeof document === 'undefined') return;
    document.documentElement.style.setProperty('font-size', '14px');
  }, [scoped]);

  // Memoize the colors object and the context value: every `useTheme()`
  // consumer in the app re-renders when this value's reference changes, so a
  // parent re-render of the provider must not mint a new object when nothing
  // theme-related actually changed.
  const colors = useMemo(() => {
    const activeTheme = Themes[activeThemeName];
    const isDark = isDarkTheme(activeThemeName);
    const resolvedAccentBg = getAccentBackgroundColor(activeThemeName, accentBackgroundColor);
    const resolvedAccentText = getAccentTextColor(accentTextColor);
    const resolvedLinkColor = ensureLinkTextContrast(
      activeTheme.text_color_link || (isDark ? '#58a6ff' : '#3157a4'),
      activeTheme.solid_color_primary,
      isDark
    );
    const baseColors = {
      ...activeTheme,
      text_color_link: resolvedLinkColor,
      accent_color_primary: resolvedAccentBg,
      accent_text_color: ensureAccentTextContrast(resolvedAccentText, resolvedAccentBg),
      text_color_user_bubble: ensureBubbleTextContrast(
        activeTheme.text_color_primary,
        activeTheme.solid_color_user_bubble
      ),
    };

    // Adjust border colors if increaseBorderContrast is enabled
    if (!increaseBorderContrast) return baseColors;
    // Desktop separators should stay quiet even with the default contrast boost.
    // Keep the stronger input outlines and mobile borders for those controls.
    const separatorAdjustment = isDesktop ? 6 : 30;
    return {
      ...baseColors,
      border_color_secondary: adjustBorderContrast(
        baseColors.border_color_secondary,
        baseColors.solid_color_primary,
        separatorAdjustment
      ),
      border_color_input: adjustBorderContrast(
        baseColors.border_color_input,
        baseColors.solid_color_primary
      ),
      border_color: adjustBorderContrast(
        baseColors.border_color_secondary, // Use secondary as fallback for border_color
        baseColors.solid_color_primary,
        separatorAdjustment
      ),
    };
  }, [activeThemeName, accentBackgroundColor, accentTextColor, increaseBorderContrast, isDesktop]);

  const value = useMemo(() => ({
    theme: activeThemeName,
    setTheme,
    accentBackgroundColor,
    setAccentBackgroundColor,
    accentTextColor,
    setAccentTextColor,
    colors,
    getBorderRadius,
  }), [activeThemeName, setTheme, accentBackgroundColor, accentTextColor, colors]);

  if (isLoading) {
    return null;
  }

  // Android reaches here before AsyncStorage resolves and paints the surface
  // default. Returning null left a dead black window on Samsung One UI.
  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}
